
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Eye, Lock, Share2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/ui/PageTransition';
import { useQuery } from '@tanstack/react-query';
// import { supabase } from '@/integrations/supabase/client';

const Index = () => {
  // Check if user is logged in - This will need to be replaced with Firebase Auth
  // const { data: session } = useQuery({
  //   queryKey: ['session'],
  //   queryFn: async () => {
  //     // const { data } = await supabase.auth.getSession(); // Needs Firebase equivalent
  //     // return data.session;
  //     return null; // Placeholder
  //   }
  // });
  const session = null; // Placeholder: Assume no session for now

  const features = [
    {
      icon: <Shield className="h-5 w-5 text-primary" />,
      title: 'Privacy Controls',
      description: 'Full control over your personal information with field-level privacy settings'
    },
    {
      icon: <Eye className="h-5 w-5 text-primary" />,
      title: 'Preview Mode',
      description: 'See exactly what others will see before sharing your profile'
    },
    {
      icon: <Lock className="h-5 w-5 text-primary" />,
      title: 'Granular Access',
      description: 'Manage who can view your sensitive information with approval system'
    },
    {
      icon: <Share2 className="h-5 w-5 text-primary" />,
      title: 'Custom Sharing',
      description: 'Generate unique links with different privacy settings for specific recipients'
    },
    {
      icon: <FileText className="h-5 w-5 text-primary" />,
      title: 'Complete Biodata',
      description: 'Create comprehensive profiles with all the details that matter'
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-32">
          <div className="container px-6 mx-auto">
            <div className="flex flex-col md:flex-row items-center">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="md:w-1/2 md:pr-12"
              >
                <div className="space-y-6 max-w-xl">
                  <div>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1 text-sm font-medium"
                    >
                      <Shield size={14} />
                      <span>Privacy-First Design</span>
                    </motion.div>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                    Your biodata, <br />
                    <span className="text-primary">your privacy</span> rules
                  </h1>
                  
                  <p className="text-lg text-muted-foreground">
                    Create, manage, and share your matrimonial profile with 
                    complete control over who sees what information.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button asChild size="lg" className="rounded-full">
                      <Link to={session ? "/profile" : "/auth"}>
                        Create Your Profile
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="md:w-1/2 mt-12 md:mt-0"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary/10 rounded-2xl blur-xl opacity-70"></div>
                  <div className="relative bg-white rounded-2xl shadow-glass overflow-hidden">
                    <div className="aspect-video w-full bg-muted/30 flex items-center justify-center">
                      <div className="text-center p-12">
                        <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                          <FileText className="h-8 w-8 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold">Preview Your Biodata</h3>
                        <p className="text-muted-foreground mt-2">Interactive previews coming soon</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* Features Section */}
        <section className="py-20 bg-secondary/50">
          <div className="container px-6 mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Privacy-First Features
              </h2>
              <p className="text-muted-foreground text-lg">
                Designed from the ground up to protect your personal information
                while making it easy to share what matters.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="bg-white p-6 rounded-xl shadow-subtle h-full flex flex-col">
                    <div className="rounded-full w-12 h-12 flex items-center justify-center bg-primary/10 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground flex-grow">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-20">
          <div className="container px-6 mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-8 md:p-12 relative overflow-hidden"
            >
              <div className="max-w-3xl relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to create your private biodata?
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Get started in minutes with our easy-to-use platform.
                  Your privacy is our priority.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="rounded-full">
                    <Link to={session ? "/profile" : "/auth"}>
                      Create Your Profile
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
              
              <div className="absolute right-0 top-0 h-full w-1/3 pointer-events-none hidden md:block">
                <div className="absolute right-12 top-1/2 -translate-y-1/2">
                  <div className="w-20 h-20 bg-primary/20 rounded-full"></div>
                  <div className="w-32 h-32 bg-primary/10 rounded-full absolute -top-40 -left-20"></div>
                  <div className="w-16 h-16 bg-primary/30 rounded-full absolute -bottom-20 left-10"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default Index;
